import axios, { AxiosResponse } from "axios";
import {
  ArrayFromEnvelope,
  configService,
  ObjectFromEnvelope
} from "./config.service";
import {
  ISupervisionTimeSchedulePost,
  ISupervisionTimeSchedulePut,
  SupervisionTimeSchedule
} from "../models/supervisionTimeSchedules";
import { SupervisionTimeDuties } from "../models/supervisionTimeDuties";
import { convertDateOnlyToUtc } from "../utils/dateUtils";

class SupervisionTimeScheduleService {
  async fetchSupervisionTimeSchedules(): Promise<SupervisionTimeSchedule[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<SupervisionTimeSchedule[]>(
          configService.APIInstitutionUrl(
            "supervision_time_schedules.json?institution_id=" +
              configService.institutionId()
          )
        )
        .then((response: AxiosResponse) => {
          resolve(
            SupervisionTimeSchedule.fromJSONArray(
              ArrayFromEnvelope<SupervisionTimeSchedule>(response.data)
            )
          );
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async fetchSupervisionTimeDuties(
    timeScheduleId: number
  ): Promise<SupervisionTimeDuties[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<SupervisionTimeDuties[]>(
          configService.APIInstitutionUrl(
            "supervision_time_duties.json?supervision_time_schedule_id=" +
              timeScheduleId
          )
        )
        .then((response: AxiosResponse) => {
          resolve(
            SupervisionTimeDuties.fromJSONArray(
              ArrayFromEnvelope<SupervisionTimeDuties>(response.data)
            )
          );
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async createSupervisionTimeSchedule(
    supervisionTimeSchedule: ISupervisionTimeSchedulePost
  ): Promise<SupervisionTimeSchedule> {
    return new Promise((resolve, reject) => {
      axios
        .post<SupervisionTimeSchedule>(
          configService.APIInstitutionUrl(
            "supervision_time_schedules/create_sts.json"
          ),
          supervisionTimeSchedule
        )
        .then(async (response: AxiosResponse) => {
          const sts: SupervisionTimeSchedule = SupervisionTimeSchedule.fromJSON(
            (response.data as any).data.supervision_time_schedule
          );
          sts.supervision_time_duties =
            await SupervisionTimeDuties.fromJSONArray(
              (response.data as any).data.supervision_time_duties
            );

          resolve(sts);
        })
        .catch((error) => reject(error));
    });
  }
  async updateSupervisionTimeSchedule(
    supervisionTimeSchedule: ISupervisionTimeSchedulePut
  ): Promise<SupervisionTimeSchedule> {
    return new Promise((resolve, reject) => {
      axios
        .post<SupervisionTimeSchedule>(
          configService.APIInstitutionUrl(
            "supervision_time_schedules/update_sts/" +
              supervisionTimeSchedule.update_supervision_time_schedule.id +
              ".json"
          ),
          supervisionTimeSchedule
        )
        .then(async (response: AxiosResponse) => {
          const data = ObjectFromEnvelope<any>(response.data);
          const sts: SupervisionTimeSchedule = SupervisionTimeSchedule.fromJSON(
            data.supervision_time_schedule
          );
          sts.supervision_time_duties =
            await SupervisionTimeDuties.fromJSONArray(
              data.supervision_time_duties
            );

          resolve(sts);
        })
        .catch((error) => reject(error));
    });
  }
  async deleteSupervisionTimeSchedule(
    supervisionTimeScheduleId: number
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete<SupervisionTimeSchedule>(
          configService.APIInstitutionUrl(
            "supervision_time_schedules/" + supervisionTimeScheduleId + ".json"
          )
        )
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }
  async rollOutSupervisionTimeScheduleByDates(
    supervisionTimeScheduleId: number,
    includedEmployees: number[],
    weekCycleIndex: number,
    dates: Date[]
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      let employeeIds = "";
      includedEmployees.forEach((e, index) => {
        employeeIds += index === includedEmployees.length - 1 ? e : e + ",";
      });
      let utcDates = "";
      dates.forEach((d, index) => {
        utcDates +=
          index === dates.length - 1
            ? convertDateOnlyToUtc(d)
            : convertDateOnlyToUtc(d) + ",";
      });
      axios
        .post<void>(
          configService.APIInstitutionUrl(
            "supervision_time_schedules/" +
              supervisionTimeScheduleId +
              "/roll_out.json?included_employees=" +
              employeeIds +
              "&week_cycle_index=" +
              weekCycleIndex
          ),
          { on_dates: utcDates }
        )
        .then(() => {
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  async rollOutSupervisionTimeScheduleByWeeks(
    supervisionTimeScheduleId: number,
    weeks: string,
    includedEmployees: number[]
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      let employeeIds = "";
      includedEmployees.forEach((e, index) => {
        employeeIds += index === includedEmployees.length - 1 ? e : e + ",";
      });
      axios
        .post<void>(
          configService.APIInstitutionUrl(
            "supervision_time_schedules/" +
              supervisionTimeScheduleId +
              "/roll_out.json?weeks=" +
              weeks +
              "&included_employees=" +
              employeeIds +
              "&week_cycle_index=1"
          )
        )
        .then(() => {
          resolve();
        })
        .catch((error) => reject(error));
    });
  }
}

export const supervisionTimeScheduleService: SupervisionTimeScheduleService =
  new SupervisionTimeScheduleService();
